import React, { useRef } from 'react';
import { usePaginationFragment } from 'react-relay/hooks';

import { createStyles, TableBody, WithStyles, withStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { compact, get, isEmpty, map } from 'lodash';

import {
  ContentLoaderTableRows,
  InfiniteScroll,
  InfiniteScrollProps,
  Table,
  TableHeadReminders,
  tableHeadRemindersCols,
  TableProps,
  TableRowEmptyState,
} from 'components/v2';
import useURLSearchParams from 'hooks/useURLSearchParams';

import { TableReminders_Query$key } from './__generated__/TableReminders_Query.graphql';
import TableRowReminder from './TableRowReminder';
import { getCleanFilter } from './utils/MessageActivityTables/getCleanFilter';
import { useFilterMessagesBySenderAndReceiver } from './hooks/useFilterMessagesBySenderAndReceiver';

const styles = () =>
  createStyles({
    root: {
      width: '100%',
    },
  });

export interface TableRemindersProps extends Omit<TableProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: TableReminders_Query$key;
  renderEmptyState?: () => React.ReactElement;
  infiniteScrollProps?: Partial<InfiniteScrollProps>;
}

const TableReminders: React.FC<TableRemindersProps> = ({
  classes,
  fragmentRef,
  renderEmptyState,
  infiniteScrollProps,
  ...rest
}) => {
  const callsRef = useRef({ numberOfCalls: 0, remindersArrayLength: 0 });
  const {
    data: { reminderConnection },
    loadNext,
    isLoadingNext,
    hasNext,
  } = usePaginationFragment<any, TableReminders_Query$key>(
    graphql`
      fragment TableReminders_Query on Query
        @argumentDefinitions(
          cursor: { type: "String" }
          count: { type: "Int", defaultValue: 15 }
          filter: { type: "FilterFindManyReminderInput" }
        )
        @refetchable(queryName: "TableReminders_refetchable") {
        reminderConnection(first: $count, after: $cursor, sort: SENDAT_ASC, filter: $filter)
          @connection(key: "TableReminders_reminderConnection") {
          edges {
            node {
              _id
              oid_facility
              ...TableRowReminder_Reminder
            }
          }
        }
      }
    `,
    fragmentRef,
  );

  const nodes = compact(map(get(reminderConnection, 'edges'), 'node'));

  const searchParams = useURLSearchParams();
  const { filterArrayWithoutAll, shouldFilterByNotIn } = getCleanFilter(searchParams?.facility);
  const reminders = useFilterMessagesBySenderAndReceiver(nodes, false, filterArrayWithoutAll, shouldFilterByNotIn);

  const shouldUpdateNumberOfCalls = reminders.length === callsRef.current.remindersArrayLength;

  callsRef.current = {
    numberOfCalls: shouldUpdateNumberOfCalls ? callsRef.current.numberOfCalls + 1 : 0,
    remindersArrayLength: reminders.length,
  };

  if (isEmpty(reminders) && !!renderEmptyState) {
    return renderEmptyState();
  }

  return (
    <InfiniteScroll
      className={classes.root}
      hasNext={hasNext && callsRef.current.numberOfCalls < 10}
      isLoadingNext={isLoadingNext}
      loadNext={() => loadNext(15)}
      {...infiniteScrollProps}
    >
      <Table {...rest}>
        <TableHeadReminders />
        <TableBody>
          {map(reminders, (reminder) => (
            <TableRowReminder fragmentRef={reminder} key={reminder._id} />
          ))}
          {isEmpty(nodes) && <TableRowEmptyState colSpan={tableHeadRemindersCols} />}
          {isLoadingNext && <ContentLoaderTableRows colSpan={tableHeadRemindersCols} />}
        </TableBody>
      </Table>
    </InfiniteScroll>
  );
};

export default withStyles(styles)(TableReminders);
